import React from 'react';
import { TableColumn } from 'react-data-table-component';
import { Button } from '@mui/material';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import moment from 'moment';
import { ShowAlert } from '../../ShowAlert';
import dayjs from "dayjs";
import Tooltip from '@mui/material/Tooltip';

interface IDataRow {
    [column: string]: string | number | Date | any;
}
type Primitive = string | number | boolean;
type Selector<T> = (row: T, rowIndex?: number) => Primitive;

export interface DownloadCsvProps<D> {
    columns: TableColumn<D>[];
    data: D[];
    csvFileName?: string;
    selectedDate: { startDate: Date | null, endDate: Date | null };
    isDatePickerNeeded: boolean;
    downlaodFor: string;
    isExportCSVForTransactionLog?: boolean;
}

export interface DownloadCsvState {
}


class DownloadCsv<D extends IDataRow, C> extends React.Component<DownloadCsvProps<D>, DownloadCsvState> {

    constructor(props_: DownloadCsvProps<D>) {
        super(props_);

        this.state = {}
    }

    public render() {
        return (
            <Tooltip title="Download CSV" arrow>
                {/* <Button onClick={e => {
                    this.downloadCSV(this.props.data);
                }}>Export</Button> */}
                <Button color="primary"
                    data-testid="download-csv-button"
                    onClick={(ev_) => {
                        this.onClickDownloadCSV(this.props.data);
                    }}
                >
                    <DownloadForOfflineIcon fontSize="large" color='secondary' />
                </Button>
            </Tooltip>
        )
    }

    private onClickDownloadCSV(array: D[]) {
        if (this.props.isExportCSVForTransactionLog) {//For transaction log report
            let startDate = moment(this.props.selectedDate.startDate);
            let endDate = moment(this.props.selectedDate.endDate);
            let dateDiff = (endDate).diff(startDate, 'days');
            if (dateDiff <= 31 && this.props.data.length >= 1) {
                this.csvForTransLogs(array);
            } else if ((dateDiff <= 31) && (this.props.data == undefined || this.props.data.length < 1)) {
                this.errormsg();
            } else if (dateDiff > 31) {
                ShowAlert("Error", "The number of days should not exceed 31.", "error")
            }
        } else {//For other reports
            if (this.props.downlaodFor === "promoUsersList" && array.length >= 1) {
                this.csvForPromoUser(array)
            } else if (array.length >= 1) {
                this.prepareCSV(array, this.props.columns);
            } else {
                this.errormsg();
            }
        }
    }

    private errormsg() {
        ShowAlert("Error", "No data to download", "error")
    }

    // This is for transaction page csv downloads...
    private csvForTransLogs(array: D[]) {
        // Define two different sets of columns as required
        const priority_transaction_column = [
            "transfer_id",
            "payment_transaction_id",
            "name",
            "sender_email",
            "sender_mobile",
            "paid_amount",
            "payment_status",
            "status_reason",
            "payment_time",
            "processed_time"
        ];

        const itc_transaction_column = [
            "transfer_id",
            "payment_transaction_id",
            "transaction_id",
            "account",
            "channel",
            "payment_method_type",
            "from_currency_rate",
            "to_currency_rate",
            "sender_currency",
            "sender_amount",
            "destination_currency",
            "destination_amount",
            "markup_percentage",
            "markup_value",
            "platform_fee_value",
            "platform_fee",
            "total_amount",
            "transaction_status",
            "created_utc",
            "modified_utc",
            "response_code",
            "response_message",
            "uniwallet_transactionId"
        ];

        // Extract and format dates
        const startDate = moment(this.props.selectedDate.startDate).format("MM-DD-YYYY");
        const endDate = moment(this.props.selectedDate.endDate).format("MM-DD-YYYY");

        // Construct filenames dynamically
        const priorityFileName = `Priority_Transactions_${startDate}_to_${endDate}.csv`;
        const itcFileName = `ITC_Transactions_${startDate}_to_${endDate}.csv`;

        let arrPriority = [];
        let arrITC = [];
        for (let itemPriority of array) {
            for (let i = 0; i < itemPriority.transaction_details.length; i++) {
                let newItem = { ...itemPriority, ...itemPriority.transaction_details[i] };
                delete newItem.transaction_details;
                if (i === 0) {
                    arrPriority.push(newItem);
                }
                arrITC.push(newItem);
            }
        }

        // Sort by created_utc
        const sortedArrayPriority = arrPriority.sort((a, b) =>
            new Date(a.created_utc as string).getTime() - new Date(b.created_utc as string).getTime()
        );
        const sortedArrayITC = arrITC.sort((a, b) =>
            new Date(a.created_utc as string).getTime() - new Date(b.created_utc as string).getTime()
        );
        // Generate and download first CSV file
        this.prepareCSVForTransLogs(sortedArrayPriority, priority_transaction_column, priorityFileName);

        // Generate and download second CSV file
        this.prepareCSVForTransLogs(sortedArrayITC, itc_transaction_column, itcFileName);
    }

    private csvForPromoUser(data: any[]) {
        // Default to first item if available
        const firstItem = data?.[0];

        const rawCouponCode = typeof firstItem?.couponCode === 'string' ? firstItem.couponCode : 'UnknownCode';
        const safeCouponCode = rawCouponCode.replace(/[^a-zA-Z0-9_-]/g, '') || 'UnknownCode';

        const fileName = `Promo_${safeCouponCode}_user_list.csv`;

        this.prepareCSV(data, this.props.columns, fileName);
    }

    private prepareCSVForTransLogs(array: D[], includedColumns: string[], fileName: string) {
        let pivotedArray = this.pivotForTransLog(array, includedColumns);
        let csv = this.toCsvDataFortransLog(pivotedArray);
        this.downloadCSV(csv, fileName);
    }

    private pivotForTransLog(arr: D[], includedColumns: string[]) {
        const header = includedColumns;

        // Declare time-related fields explicitly for formats..
        const timeFields = [
            "payment_time",
            "processed_time",
            "created_utc",
            "modified_utc"
        ];

        const rows = arr.map(obj =>
            header.map(key => {
                let value = obj[key] ?? ''; // Default to empty string if value is missing

                if (key === "mobileNumber" && typeof value === "string") {
                    value = `="${value}"`;
                }

                // Convert only time fields
                if (timeFields.includes(key)) {
                    value = this.formatToLocalTime(value);
                }

                return value;
            })
        );
        return [header, ...rows];
    }

    private formatToLocalTime(utcString: string): string {
        if (!utcString) return ''; // Handle empty or invalid values

        return dayjs(utcString).format("DD-MM-YYYY HH:mm");
    }

    private toCsvDataFortransLog(arr: any[][]) {
        return arr.map(row =>
            row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(',')
        ).join('\n');
    }

    private toCsv(arr: D[][]) {
        return arr.map(row =>
            row.map((val: any) => isNaN(val) ? JSON.stringify(val) : +val).join(',')
        ).join('\n');
    }

    private prepareCSV(array: D[], columns: TableColumn<D>[], fileNameStatic: string | null = null) {
        let csv = this.toCsv(this.getPivotData(array, columns));
        const fileName = `${this.props.csvFileName && this.props.csvFileName !== '' ? this.props.csvFileName : (fileNameStatic ? fileNameStatic : 'export')}.csv`;
        this.downloadCSV(csv, fileName);
    }

    private downloadCSV(csv: string, fileName: string) {
        const link = document.createElement('a');
        if (csv == null) return;
        if (!csv.match(/^data:text\/csv/i)) {
            csv = `data:text/csv;charset=utf-8,${csv}`;
        }
        link.setAttribute('href', encodeURI(csv));
        link.setAttribute('download', fileName);
        link.click();
    }

    private convertArrayOfObjectsToCSV(array: D[]) {
        let result: any;

        const columnDelimiter = ',';
        const lineDelimiter = '\n';
        const keys = Object.keys(array[0]);

        result = '';
        result += keys.join(columnDelimiter);
        result += lineDelimiter;

        array.forEach(item => {
            let ctr = 0;
            keys.forEach(key => {
                if (ctr > 0) result += columnDelimiter;

                result += item[key];

                ctr++;
            });
            result += lineDelimiter;
        });

        return result;
    }

    private getPivotData(data: D[], columns: TableColumn<D>[]) {
        let header: string[] = [];
        let arrID: string[] = [];
        let objIsMobileField: Record<string, boolean | undefined> = {};
        let objSelector: Record<string, (Selector<D> | undefined)> = {};
        columns.forEach(column => {
            if (column.hide !== 1 && column.id) {
                let columnId = (column.id as string);
                arrID.push(columnId);
                header.push((column.name || "").toString());
                objSelector[columnId] = column.selector;
                objIsMobileField[columnId] = column.compact;//compact is used to identify whther it is mobile field or not
            }
        })
        const rows = data.map(obj =>
            arrID.map(id => {
                let value = obj[id] ?? ''; // Default to empty string if value is missing
                //For formatting the values
                if (objSelector[id]) {
                    value = (objSelector[id] as Selector<D>)(obj);
                }
                if (objIsMobileField[id]) {
                    value = value.replace(/-/g, ' ');
                }
                return value;
            })
        );
        return [header, ...rows];
    }
}

export default DownloadCsv;